import { getHyperlinkQuery } from '~/utils/getHyperlinkQuery';
import { useDownloadStore } from '~/stores/downloadStore/softwareDownloadStore';
import type {
  HyperlinkInterface,
  DownloadCardsItem as Item,
  TagInterface
} from '@hypercodestudio/basler-components';

import type {
  SoftwareEntryModel,
  SoftwareGroupedAssetModel
} from '~~/lib/DownloadService/model';
import { useSoftwareOsLabel } from '~~/lib/DownloadService/helper/useSoftwareOsLabel';
import { generateImageObject } from '~/utils/generateImageObject';
import { formatSoftwareTotalCountLabel } from '~/utils/downloads/formatSoftwareTotalCountLabel';
import { DOWNLOAD_CENTER_QUERY_PARAM } from '~/utils/algolia/getDownloadCenterSearchQuery';

type LinkBuilder = (
  softwareItem: SoftwareEntryModel,
  searchParams: URLSearchParams
) => string;

function findPlatforms(
  groupAssets?: ReadonlyArray<SoftwareGroupedAssetModel>
): string[] {
  if (!groupAssets) {
    return [];
  }

  const platforms: string[] = [];
  for (const asset of groupAssets) {
    if (!asset.supportedOs || !Array.isArray(asset.supportedOs)) {
      continue;
    }
    for (const os of asset.supportedOs) {
      if (!platforms.includes(os)) {
        platforms.push(os);
      }
    }
  }

  return platforms;
}

const LOADING_PLACEHOLDER: Item[] = Array.from({ length: 6 }, (_, i) => ({
  id: String(i + 1),
  header: {
    type: 'image',
    content: { src: 'loading', srcset: 'loading', alt: 'loading' }
  },
  title: 'loading',
  subtitle: 'loading',
  platformsLabel: 'loading',
  platforms: undefined,
  descriptionLabel: undefined,
  description: undefined,
  link: undefined
}));

export function useSoftwareListData(buildLink: LinkBuilder) {
  const store = useDownloadStore();
  const router = useRouter();
  const route = useRoute();

  const { $textDictionary, $dictionary } = useNuxtApp();
  const htmlDictionary = useHTMLDictionary(['softwareCenter.totalCount.label']);

  const noResultsRichText =
    $dictionary.value['softwareCenter.searchResults.noResults'];

  const softwareCountInformation = computed(() => {
    if (store.loading || store.softwareDownloads?.maxCount === undefined) {
      return '';
    }

    const maxCount = store.softwareDownloads.maxCount;
    const count = store.softwareDownloads.results?.length ?? 0;
    const start = count === 0 ? 0 : 1;

    const template =
      htmlDictionary.value['softwareCenter.totalCount.label'] ??
      $textDictionary.value['softwareCenter.totalCount.label'];

    return formatSoftwareTotalCountLabel({
      template,
      start,
      count,
      total: maxCount
    });
  });

  const formattedFilterFacets = computed((): TagInterface[] => {
    const validFilters: Record<string, string[]> = {};

    for (const key in store.softwareDownloads?.filters) {
      const k = Number(key);
      const filtername = store.softwareDownloads?.filters[k].name;
      if (k >= 0 && filtername && store.filters[filtername]) {
        validFilters[filtername] = store.filters[filtername];
      }
    }

    const facets: TagInterface[] = [];
    for (const key in validFilters) {
      for (const filter of validFilters[key]) {
        const filterLabelPrefix = 'softwareCenter.softwareFilter.';
        const filterLabel =
          $textDictionary.value[filterLabelPrefix + key + '.label'] ?? key;
        facets.push({
          text: filterLabel + ': ' + useSoftwareOsLabel(filter),
          showCloseIcon: true,
          element: 'button',
          link: {
            uri: store.createNewUrlParamsFromFilter(key, filter)
          } satisfies HyperlinkInterface
        });
      }
    }
    return facets;
  });

  const formattedData = computed<Item[]>(() => {
    const searchParams = new URLSearchParams();
    Object.entries(store.filters).forEach(([key, values]) => {
      values.forEach((value) => searchParams.append(key, value));
    });
    if (store.query) {
      searchParams.set(DOWNLOAD_CENTER_QUERY_PARAM, store.query);
    }

    const results = store.softwareDownloads?.results?.map<Item>(
      (softwareItem: SoftwareEntryModel) => ({
        id: softwareItem.id,
        header: softwareItem.image
          ? {
              type: 'image',
              content: {
                width: 400,
                srcset: softwareItem.image + ' 640w',
                alt: softwareItem.name,
                src:
                  generateImageObject(
                    {
                      url: softwareItem.image,
                      altTag: softwareItem.name
                    },
                    {
                      quality: 100,
                      width: 400,
                      keepAspectRatio: false
                    }
                  )?.content?.src ?? softwareItem.image
              }
            }
          : undefined,
        title: softwareItem.name ?? '',
        subtitle: softwareItem.softwareVersion
          ? `${
              $textDictionary.value[
                'softwareCenter.downloadCard.versionLabel'
              ] ?? 'Version'
            } ${softwareItem.softwareVersion} (${softwareItem.dateYear})`
          : `(${softwareItem.dateYear})`,
        platformsLabel:
          softwareItem.supportedOs && softwareItem.supportedOs?.length
            ? ($textDictionary.value[
                'softwareCenter.downloadCard.platformsLabel'
              ] ?? 'Available for:')
            : undefined,
        platforms: findPlatforms(softwareItem.groupAssets).map((os) => ({
          iconName: os === 'macOs' ? 'mac' : os,
          text: os
        })),
        link: {
          uri: buildLink(softwareItem, searchParams)
        },
        // PDP opens modals via hash (#software=) and SoftwareCenterStandaloneModal;
        // only the standalone software center uses the @open event + pushState.
        isModal: store.context === 'softwareCenter'
      })
    );

    return results ?? LOADING_PLACEHOLDER;
  });

  function removeTag(tag: TagInterface) {
    const query = getHyperlinkQuery(tag.link);
    if (!query) {
      return;
    }
    void router.replace({
      path: route.path,
      hash: route.hash,
      query,
      replace: true
    });
  }

  return {
    softwareCountInformation,
    noResultsRichText,
    formattedFilterFacets,
    formattedData,
    removeTag
  };
}
